import { lazy, Suspense } from "react";
import { Navigate, Route, Routes, useLocation } from "react-router";
import { LoginForm } from "./components/auth/LoginForm";
import { AppLayout } from "./components/layout/AppLayout";
import { GuardiaDeInactividad } from "./components/layout/GuardiaDeInactividad";
import { authClient } from "./lib/auth-client";
import { useSession } from "./lib/SessionContext";
import { AuditoriaPagina } from "./paginas/auditoria/AuditoriaPagina";
import { CatalogosPagina } from "./paginas/catalogos/CatalogosPagina";
import { ClientePagina } from "./paginas/clientes/ClientePagina";
import { ClientesPagina } from "./paginas/clientes/ClientesPagina";
import { ConfiguracionPagina } from "./paginas/configuracion/ConfiguracionPagina";
import { OlvideContrasenaPagina } from "./paginas/contrasena/OlvideContrasenaPagina";
import { RestablecerContrasenaPagina } from "./paginas/contrasena/RestablecerContrasenaPagina";
import { FacturacionPagina } from "./paginas/facturacion/FacturacionPagina";
import { FlagsPagina } from "./paginas/flags/FlagsPagina";
import { ImpresoraPagina } from "./paginas/impresoras/ImpresoraPagina";
import { ImpresorasPagina } from "./paginas/impresoras/ImpresorasPagina";
import { InicioPagina } from "./paginas/inicio/InicioPagina";
import { AceptarInvitacionPagina } from "./paginas/invitacion/AceptarInvitacionPagina";
import { PerfilPagina } from "./paginas/perfil/PerfilPagina";
import { AumentoPagina } from "./paginas/precios/AumentoPagina";
import { PreciosPagina } from "./paginas/precios/PreciosPagina";
import { ConsultaPagina } from "./paginas/presupuestos/ConsultaPagina";
import { EditarConsultaPagina } from "./paginas/presupuestos/EditarConsultaPagina";
import { NuevaConsultaPagina } from "./paginas/presupuestos/NuevaConsultaPagina";
import { PresupuestosPagina } from "./paginas/presupuestos/PresupuestosPagina";
import { CalendarioPagina } from "./paginas/produccion/CalendarioPagina";
import { NuevaOtPagina } from "./paginas/produccion/NuevaOtPagina";
import { OrdenPagina } from "./paginas/produccion/OrdenPagina";
import { TableroPagina } from "./paginas/produccion/TableroPagina";
import { ProductoPagina } from "./paginas/productos/ProductoPagina";
import { ProductosPagina } from "./paginas/productos/ProductosPagina";
import { RolesPagina } from "./paginas/roles/RolesPagina";
import { RolPagina } from "./paginas/roles/RolPagina";
import { EstadoSistemaPagina } from "./paginas/sistema/EstadoSistemaPagina";
import { UsuarioPagina } from "./paginas/usuarios/UsuarioPagina";
import { UsuariosPagina } from "./paginas/usuarios/UsuariosPagina";

/**
 * Shell del dashboard.
 *
 * La navegacion se arma con `can()`, la MISMA funcion que usa el worker para
 * autorizar. Pero ocultar no es autorizar (invariante 5): cada endpoint aplica
 * su propio `requirePermission`, y esconder el link solo evita que alguien
 * llegue a una pantalla que le va a dar 403.
 */

/**
 * Reportes se carga aparte: es la unica pantalla que usa `recharts`, y quien
 * nunca la abre no tiene por que descargarlo (ADR 0031). Es tambien la unica
 * ruta con `lazy` — el resto son pantallas chicas sobre los mismos primitivos.
 */
const ReportesPagina = lazy(() =>
  import("./paginas/reportes/ReportesPagina").then((m) => ({ default: m.ReportesPagina })),
);

export function App() {
  const { me, cargando, can, canCodigo, recargar } = useSession();
  const { pathname } = useLocation();

  // Aceptar una invitacion es lo unico que se ve SIN sesion aparte del login
  // (ADR 0035): quien llega ahi todavia no tiene cuenta. Va antes del chequeo de
  // sesion porque abajo, sin `me`, la app muestra el formulario de login — y ahi
  // el enlace del mail moriria en una pantalla que pide credenciales que la
  // persona todavia no tiene.
  if (pathname === "/aceptar-invitacion") return <AceptarInvitacionPagina />;

  // Igual con el reset de contraseña (ADR 0104 §5): el enlace del mail llega sin
  // sesión y moriría en el login.
  if (pathname === "/olvide-contrasena") return <OlvideContrasenaPagina />;
  if (pathname === "/restablecer-contrasena") return <RestablecerContrasenaPagina />;

  if (cargando) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-neutral-50 text-neutral-600">
        Cargando...
      </div>
    );
  }

  if (!me) return <LoginForm />;

  const salir = async () => {
    await authClient.signOut();
    await recargar();
  };

  return (
    <GuardiaDeInactividad email={me.email} alSalir={salir}>
      <AppLayout
        puede={canCodigo}
        nombre={me.nombre}
        email={me.email}
        roles={me.roles}
        alSalir={salir}
      >
        <Routes>
          {/* Inicio. No pide permiso: el endpoint decide que tarjetas manda segun
            lo que la persona puede ver, y si no puede ver ninguna la pantalla lo
            dice en vez de mostrarse vacia. */}
          <Route path="/" element={<InicioPagina />} />
          {/* El perfil NO se condiciona a ningun permiso: es de la persona, no
            un recurso administrable. Con `can(...)` un Operario no podria ni
            cambiarse la contraseña. */}
          <Route path="/perfil" element={<PerfilPagina />} />
          {can("usuarios", "read") ? <Route path="/usuarios" element={<UsuariosPagina />} /> : null}
          {can("usuarios", "read") ? (
            <Route path="/usuarios/:id" element={<UsuarioPagina />} />
          ) : null}
          {can("roles", "read") ? <Route path="/roles" element={<RolesPagina />} /> : null}
          {/* `/nuevo` va primero por legibilidad; react-router ya prioriza
              el segmento estático sobre el dinámico. */}
          {can("roles", "create") ? <Route path="/roles/nuevo" element={<RolPagina />} /> : null}
          {can("roles", "read") ? <Route path="/roles/:nombre" element={<RolPagina />} /> : null}
          {can("produccion", "read") ? (
            <Route path="/produccion" element={<TableroPagina />} />
          ) : null}
          {can("produccion", "read") ? (
            <Route path="/produccion/calendario" element={<CalendarioPagina />} />
          ) : null}
          {can("produccion", "create") ? (
            <Route path="/produccion/nueva" element={<NuevaOtPagina />} />
          ) : null}
          {/* Va después de `/nueva` y `/calendario` por legibilidad: react-router
              ya prioriza el segmento estático sobre el dinámico, pero leerlo en
              este orden evita que alguien agregue `/produccion/algo` arriba y
              crea que lo tapa. */}
          {can("produccion", "read") ? (
            <Route path="/produccion/:id" element={<OrdenPagina />} />
          ) : null}
          {can("presupuestos", "create") ? (
            <Route path="/presupuestos/nueva" element={<NuevaConsultaPagina />} />
          ) : null}
          {can("presupuestos", "read") ? (
            <Route path="/presupuestos" element={<PresupuestosPagina />} />
          ) : null}
          {can("presupuestos", "update") ? (
            <Route path="/presupuestos/:id/editar" element={<EditarConsultaPagina />} />
          ) : null}
          {can("presupuestos", "read") ? (
            <Route path="/presupuestos/:id" element={<ConsultaPagina />} />
          ) : null}
          {can("facturacion", "read") ? (
            <Route path="/facturacion" element={<FacturacionPagina />} />
          ) : null}
          {can("clientes", "read") ? <Route path="/clientes" element={<ClientesPagina />} /> : null}
          {can("clientes", "create") ? (
            <Route path="/clientes/nuevo" element={<ClientePagina />} />
          ) : null}
          {can("clientes", "update") ? (
            <Route path="/clientes/:id/editar" element={<ClientePagina />} />
          ) : null}
          {can("impresoras", "read") ? (
            <Route path="/impresoras" element={<ImpresorasPagina />} />
          ) : null}
          {can("impresoras", "read") ? (
            <Route path="/impresoras/:id" element={<ImpresoraPagina />} />
          ) : null}
          {can("productos", "read") ? (
            <Route path="/productos" element={<ProductosPagina />} />
          ) : null}
          {can("productos", "read") ? (
            <Route path="/productos/:id" element={<ProductoPagina />} />
          ) : null}
          {can("precios", "read") ? <Route path="/precios" element={<PreciosPagina />} /> : null}
          {can("precios", "bulk_update") ? (
            <Route path="/precios/aumentar" element={<AumentoPagina />} />
          ) : null}
          {can("catalogos", "read") ? (
            <Route path="/catalogos" element={<CatalogosPagina />} />
          ) : null}
          {can("reportes", "negocio") ? (
            <Route
              path="/reportes"
              element={
                <Suspense
                  fallback={
                    <p className="py-12 text-center text-neutral-500 text-sm">
                      Cargando reportes...
                    </p>
                  }
                >
                  <ReportesPagina />
                </Suspense>
              }
            />
          ) : null}
          {can("auditoria", "read") ? (
            <Route path="/auditoria" element={<AuditoriaPagina />} />
          ) : null}
          {can("sistema", "config.read") ? <Route path="/flags" element={<FlagsPagina />} /> : null}
          {can("sistema", "config.read") ? (
            <Route path="/configuracion" element={<ConfiguracionPagina />} />
          ) : null}
          {/* `metricas` y no `config.read`: mirar el estado no habilita a tocar
            nada, y el catalogo de `imprenta` ya separaba los dos permisos. */}
          {can("sistema", "metricas") ? (
            <Route path="/sistema" element={<EstadoSistemaPagina />} />
          ) : null}
          {/* Cualquier otra cosa vuelve al inicio. Antes iba al primer item
              del menu, porque no habia inicio adonde ir. */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </AppLayout>
    </GuardiaDeInactividad>
  );
}
